
import {map, tap, withLatestFrom, filter} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ADMIN_ROLE } from '@careapp/core';
import { Actions, Effect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ROUTER_NAVIGATION } from '@ngrx/router-store';

import { CAAction } from '../actions';
import * as actions from '../actions/index';
import * as selectors from '../reducers/index';

@Injectable() /**
 * Effects class for changing routes when an action occurs
 */
export class RouterEffects {
  @Effect({ dispatch: false })
  navigate$ = this.actions$
    .ofType(actions.GO).pipe(
    map(action => action.payload),
    tap(({ path, query: queryParams, extras }) =>
      this.router.navigate(path, { queryParams, ...extras })
    ),);

  @Effect({ dispatch: false })
  organisationCreated = this.actions$
    .ofType(actions.Organisations.RESPONSE_SUCCESS_CREATE).pipe(
    tap(action => {
      this.router.navigate(['/', 'admin', 'organisations']);
    }));

  @Effect({ dispatch: false })
  routerEffects = this.actions$
    .ofType(ROUTER_NAVIGATION).pipe(
    withLatestFrom(
      this.store.select(selectors.isAppAuthorized),
      this.store.select(selectors.getRouterState),
      this.store.select(selectors.getSelectedId('organisations')),
      this.store.select(selectors.auth.isAdmin),
      this.store.select(selectors.org.getSelectableIds)
    ),
    filter(([_, auth]) => !!auth),
    tap(([action, authorized, router, selectedOrganisation, admin, orgIds]) => {
      if (router.state) {
        switch (router.state.url) {
          case '/': {
            if (selectedOrganisation) {
              this.store.dispatch(
                actions.NavigateAction({
                  path: ['/', 'organisations', selectedOrganisation]
                })
              );
            } else if (admin) {
              this.store.dispatch(actions.NavigateAction({ path: [`/admin`] }));
            } else {
              this.store.dispatch(
                actions.NavigateAction({
                  path: [`/organisations`, orgIds[0]]
                })
              );
            }
          }
        }
      }
    }),);

  @Effect({ dispatch: false })
  initialRedirect = this.actions$
    .ofType(actions.APPLICATION_READY).pipe(
    withLatestFrom(
      this.store.select(selectors.getRouterState),
      this.store.select(selectors.getAuthenticationState),
      this.store.select(selectors.getSelectedId('organisations')),
      this.store.select(selectors.org.getSelectableIds)
    ),
    tap(([action, router, auth, orgId, orgIds]) => {
      orgId = orgId || orgIds[0];
      if (!router || (router.state && router.state.url !== '/auth' && !auth.auth)) {
        return this.store.dispatch(
          actions.NavigateAction({
            path: ['/', 'auth'],
            extras: { skipLocationChange: true, replaceUrl: true }
          })
        );
      }
      if (
        router.state &&
        router.state.url &&
        !router.state.url.match(/auth$/) &&
        router.state.url !== '/'
      ) {
        return;
      }
      if (auth.roles && auth.roles.find(value => value === ADMIN_ROLE)) {
        this.router.navigate(['/admin']);
      } else if (orgId) {
        this.router.navigate(['/organisations', orgId]);
      } else {
        console.log('No organisation information available - falling back to login');
        actions.NavigateAction({
          path: ['/', 'auth']
        });
      }
    }),);

  @Effect({ dispatch: false })
  customerCreated = this.actions$.ofType(actions.Customers.RESPONSE_SUCCESS_CREATE).pipe(tap(action => {
    this.router.navigate(['/organisations', (<any>action).payload.organisationId, 'customers']);
  }));

  @Effect({ dispatch: false })
  redirectToLogin = this.actions$
    .ofType(actions.RESPONSE_SUCCESS_AUTH_LOGOUT, actions.RESPONSE_FAIL_AUTH_LOGOUT).pipe(
    tap(action => this.router.navigate(['/', 'auth'])));

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private router: Router,
    private route: ActivatedRoute
  ) {}
}
